import { get, set } from 'lodash';
import { ACCOUNT_ID, DIMENSION } from 'hooks';
import { campaignNameValidations, CAMPAIGNS_ROUTE, ENTITY } from 'modules/campaigns/config';
import { REPORT_ENTITY_IDS } from 'modules/campaigns/hooks/useReportEntityIds';
import { REPORT_ENTITY_TYPE } from 'modules/campaigns/hooks/useReportEntityType';
import { normalizeColumnDefs } from 'modules/campaigns/modules/campaigns-reports/utils';
import { selectCampaignAndReport } from '../../../../../flows';
import {
    ActionableTextWithInternalLinkCellRenderer,
    CampaignActionsCellRenderer,
    CampaignStatusRenderer,
    FormattedMessageHeaderRenderer,
    FormattedMessageWithTooltipHeaderRenderer,
    formattedNumberValueGetter,
    gridNumberFormatter,
    StateCellRenderer,
} from '../../../components';
import { duplicateCampaign, saveCampaign } from '../../../flows';
import { DATE_PRESET, END_DATE, REPORT_ID, START_DATE } from '../../../hooks';
import {
    actionsColumnType,
    editableColumn,
    keyColumn,
    leftPinnedColumn,
    lockedColumn,
    numericColumn,
    rightPinnedColumn,
    staticColumn,
    toggleColumn,
} from '../../columns';
import * as FIELDS from '../../fields';
import { nonDefaultHeaderComponentParams } from '../../fields';
import { isCampaignEditable, isVideoBudgetEditable, isVideoCampaignEditable } from '../../predicates';
import { CREATIVE_VIDEO_REPORT_ID } from '../creative-video/creativeVideoReportId';
import { CAMPAIGN_VIDEO_REPORT_ID } from './campaignVideoReportId';

const campaignNameClickAction = campaign => selectCampaignAndReport(campaign, CREATIVE_VIDEO_REPORT_ID);
const isDisabled = data => !isCampaignEditable({ data });

const campaignReportColumnDefinition = [
    ...[
        {
            headerName: FIELDS.IS_ACTIVE.label,
            field: FIELDS.IS_ACTIVE.field,
            type: [toggleColumn, leftPinnedColumn, staticColumn, lockedColumn],
            cellRendererParams: {
                ...FIELDS.IS_ACTIVE.renderParams,
                isDisabled,
                reportId: CAMPAIGN_VIDEO_REPORT_ID,
            },
            cellRenderer: StateCellRenderer,
            csv: false,
            permissions: {
                permissions: ['ADS_CONSOLE_CREATE_VIDEO_CAMPAIGN'],
            },
        },
        {
            headerName: FIELDS.ID.label,
            field: FIELDS.ID.field,
            type: [numericColumn],
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: nonDefaultHeaderComponentParams(CAMPAIGN_VIDEO_REPORT_ID),
        },
        {
            headerName: FIELDS.NAME.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.NAME.headerComponentParams,
            field: FIELDS.NAME.field,
            type: [leftPinnedColumn, keyColumn, editableColumn],
            editable: isVideoCampaignEditable,
            cellRenderer: ActionableTextWithInternalLinkCellRenderer,
            cellRendererParams: {
                lines: 2,
                to: CAMPAIGNS_ROUTE,
                preventRouting: true,
                clickAction: campaignNameClickAction,
                paramsToKeep: [ACCOUNT_ID, DIMENSION, START_DATE, END_DATE, DATE_PRESET],
                paramsFactory: ({ id, accountId }) => ({
                    [ACCOUNT_ID]: accountId,
                    [REPORT_ID]: CREATIVE_VIDEO_REPORT_ID,
                    [REPORT_ENTITY_IDS]: id,
                    [REPORT_ENTITY_TYPE]: ENTITY.CAMPAIGN,
                }),
            },
            cellEditor: FIELDS.NAME.cellEditor,
            cellEditorParams: {
                validations: campaignNameValidations,
                editableTextClassName: 'with-formatted-spaces',
            },
        },
        {
            headerName: FIELDS.STATUS.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.STATUS.headerComponentParams,
            field: FIELDS.STATUS.field,
            type: [leftPinnedColumn],
            cellRenderer: CampaignStatusRenderer,
            cellRendererParams: FIELDS.STATUS.renderParams,
            csvValueGetter: FIELDS.STATUS.csvValueGetter,
            valueGetter: params => ({ status: get(params, 'data.status'), isActive: get(params, 'data.isActive') }),
        },
        {
            headerName: FIELDS.VIDEO_MODEL_TYPE.label,
            field: FIELDS.VIDEO_MODEL_TYPE.field,
            valueGetter: 'data.videoBudget.businessModel',
            valueSetter: params => set(params, 'data.videoBudget.businessModel', +params.newValue),
        },
        {
            headerName: FIELDS.VIDEO_BUDGET.label,
            headerValueGetter: FormattedMessageWithTooltipHeaderRenderer,
            headerComponentParams: FIELDS.VIDEO_BUDGET.headerComponentParams,
            field: FIELDS.VIDEO_BUDGET.field,
            type: [numericColumn, editableColumn],
            editable: isVideoBudgetEditable,
            cellRenderer: FIELDS.VIDEO_BUDGET.cellRenderer,
            cellRendererParams: FIELDS.VIDEO_BUDGET.renderParams,
            csv: FIELDS.VIDEO_BUDGET.csv,
            cellEditor: FIELDS.VIDEO_BUDGET.cellEditor,
            valueGetter: 'data.videoBudget.budget',
            cellEditorParams: {
                fieldPath: 'videoBudget.budget',
            },

            valueSetter: params => set(params, 'data.videoBudget.budget', +params.newValue),
        },
        {
            headerName: FIELDS.START_DATE.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.START_DATE.headerComponentParams,
            field: FIELDS.START_DATE.field,
            valueFormatter: FIELDS.START_DATE.valueFormatter,
            csvValueGetter: FIELDS.START_DATE.csvValueGetter,
        },
        {
            headerName: FIELDS.END_DATE.label,
            headerValueGetter: FormattedMessageHeaderRenderer,
            headerComponentParams: FIELDS.END_DATE.headerComponentParams,
            field: FIELDS.END_DATE.field,
            type: [editableColumn],
            editable: isVideoCampaignEditable,
            valueFormatter: FIELDS.END_DATE.valueFormatter,
            csvValueGetter: FIELDS.END_DATE.csvValueGetter,
            cellEditor: FIELDS.END_DATE.cellEditor,
            cellEditorParams: {
                placeholderMsgId: 'campaign.time.frame.no.end.date',
                defaultPlaceholderMsg: 'No End Date',
                inputIconMsgId: 'campaign.time.frame.end',
                defaultInputIconMsg: 'End',
            },
        },
    ].map(colDef => ({ valueChangeHandler: saveCampaign, ...colDef })),
    ...[
        {
            ...FIELDS.SPENT,
            renderParams: {
                ...FIELDS.SPENT.renderParams,
                getTotalValue: data => get(data, 'videoBudget.budget'),
            },
        },
        FIELDS.COMPLETED_VIEWS,
        FIELDS.VIDEO_UNIQUE_VIEWABLE_IMPRESSIONS,
        FIELDS.VIDEO_UNIQUE_IMPRESSIONS,
        FIELDS.UNIQUE_COMPLETED_VIEWS,
        FIELDS.VIDEO_IMPRESSIONS,
        FIELDS.VIDEO_VISIBLE_IMPRESSIONS,
        FIELDS.VIDEO_CPM,
        FIELDS.VIDEO_VCPM,
        FIELDS.CPCV,
        FIELDS.VIEWABILITY_RATE,
        FIELDS.HUNDRED_PCT_VIEWABILITY_RATE,
        FIELDS.COMPLETION_RATE,
        FIELDS.CLICKS,
        FIELDS.VIDEO_CTR,
        FIELDS.VIDEO_VCTR,
        FIELDS.CPA,
        FIELDS.VIDEO_CVR,
        FIELDS.CONVERSIONS,
    ].map(fieldConfig => ({
        headerName: fieldConfig.label,
        headerValueGetter: fieldConfig.headerValueGetter || FormattedMessageWithTooltipHeaderRenderer,
        headerComponentParams: fieldConfig.headerComponentParams,
        field: fieldConfig.field,
        type: [numericColumn],
        cellRenderer: fieldConfig.cellRenderer,
        valueFormatter: !fieldConfig.cellRenderer && gridNumberFormatter,
        cellRendererParams: fieldConfig.cellRendererParams || fieldConfig.renderParams,
        csvValueGetter: fieldConfig.csvValueGetter || formattedNumberValueGetter,
        referenceColId: fieldConfig.referenceColId,
        permissions: fieldConfig.permissions,
        visibilityCondition: fieldConfig.visibilityCondition,
    })),
    {
        headerName: FIELDS.ACTIONS_COLUMN.label,
        field: FIELDS.ACTIONS_COLUMN.field,
        type: [rightPinnedColumn, actionsColumnType, staticColumn],
        cellRendererParams: {
            onDuplicate: duplicateCampaign,
            onTogglePolicyState: saveCampaign,
        },
        cellRenderer: CampaignActionsCellRenderer,
        csv: false,
        minWidth: 100,
        maxWidth: 150,
        width: 150,
        permissions: {
            permissions: ['ADS_CONSOLE_CREATE_VIDEO_CAMPAIGN'],
        },
    },
];

export default normalizeColumnDefs(campaignReportColumnDefinition, CAMPAIGN_VIDEO_REPORT_ID);
